import { Component, ViewEncapsulation } from '@angular/core';
import { models } from 'powerbi-client';
import { ReportService } from '../../../services/reports/report.service';
import { TabDataService } from '../../../services/tab-data.service';
import {
  ReportEmbedEntry,
  buildReportParamsQueryString,
  populateReportConfigMap,
  findReportEntryById,
  getReportParamsFromUrlOrStorage
} from '../report-embed.utils';

/** Name of the hidden URL table visual in the Power BI Dashboard. Must exist on the active page. */
const HIDDEN_URL_TABLE_VISUAL_NAME = 'HiddenURLTable';

/**
 * Maps each dashboard button title (normalised to uppercase) to the corresponding
 * column header in the HiddenURLTable visual.  Each column contains a fully-formed
 * URL (with reportId and rp:* params) specific to that button's filtered view.
 */
const BUTTON_TITLE_TO_COLUMN: Record<string, string> = {
  'UNASSIGNED > 1 HR':       'Unassigned',
  'RESPONDED < 2 HR':        'Responded',
  'RESOLVED < 24 HR':        'Resolved',
  'POTENTIAL ESCALATIONS':   'Potential_Esc',
  'ESCALATIONS >3 DAYS':     'Escalation_Gtr',
  'EXTERNAL QUERIES':        'External_Queries',
  'INTERNAL QUERIES':        'Internal_Queries',
  'NON INQUIRIES':           'Non_Inquiries',
  'AGED > 3 DAYS':           'Aged_Over_3D',
  'INQUIRY WITH 3+ GROUPS':  'Inquiry_3Plus'
};

@Component({
  selector: 'app-mis-report',
  templateUrl: './mis-report.component.html',
  styleUrls: ['./mis-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MisReportComponent {
  /** Tabs for MIS Dashboard and other reports */
  public reportTabs: { reportId: string; label: string; embedConfig: ReportEmbedEntry }[] = [];
  /** Index of the currently selected report tab */
  public selectedReportTabIndex = 0;
  reportConfig = {
    type: 'report' as const,
    id: '',
    embedUrl: '',
    accessToken: '',
    tokenType: models.TokenType.Embed,
    hostname: "https://app.powerbi.com",
    settings: {
      panes: {
        filters: { visible: false },
        pageNavigation: { visible: false }
      },
      // Intercept hyperlink clicks so linked reports open in a new in-app tab (RaiseEvent = 2)
      hyperlinkClickBehavior: 2
    }
  };
  showSpinner = false;
  /** Handlers for Power BI embed events. */
  eventHandlers = new Map<string, (event?: any, entity?: unknown) => void | null>();

  private readonly reportConfigByReportId = new Map<string, ReportEmbedEntry>();

  /** Resolved rp:* params (from URL or sessionStorage) applied at init time — used as fallback. */
  private readonly reportParams: Record<string, string | string[]>;

  /**
   * Embedded Power BI report instance, captured from the `loaded` / `rendered` events.
   * Used to call the JS API (getPages, getVisuals, exportData) for button clicks.
   */
  private embeddedReport: any = null;

  /**
   * The HiddenURLTable visual object, cached from the `rendered` event via getPages()+getVisuals().
   * - `undefined` = rendered not yet fired; cache cold
   * - `null`      = rendered fired but visual not found (not yet added in Power BI Desktop)
   * - object      = the visual — call exportData() on it at button-click time for current slicer state
   *
   * NOTE: exportData() is intentionally NOT called here. Calling it from rendered causes
   * Power BI to re-render, which fires rendered again → infinite loop.
   */
  private cachedHiddenTableVisual: any | null | undefined = undefined;

  /**
   * Tracks reportIds currently being fetched to prevent duplicate API calls.
   * The buttonClicked/dataHyperlinkClicked events can fire multiple times if the
   * embed reinitializes and re-registers handlers — this guard dedups concurrent fetches.
   */
  private readonly fetchInProgress = new Set<string>();


  constructor(
    private readonly reportService: ReportService,
    private readonly tabDataService: TabDataService
  ) {
    this.eventHandlers.set('loaded', (_event, entity) => {
      this.embeddedReport = entity;
    });
    // After all visuals render, cache the HiddenURLTable visual object.
    // Only getPages()+getVisuals() are called here — exportData() is intentionally deferred
    // to button-click time to avoid triggering a re-render loop.
    this.eventHandlers.set('rendered', (_event, entity) => {
      if (entity) this.embeddedReport = entity;
      this.cachedHiddenTableVisual = undefined; // invalidate on each new render
      const embed = (entity ?? this.embeddedReport) as any;
      if (!embed) { this.cachedHiddenTableVisual = null; return; }
      embed.getPages().then((pages: any[]) => {
        const activePage = pages?.find((p: any) => p.isActive);
        if (!activePage) { this.cachedHiddenTableVisual = null; return; }
        activePage.getVisuals().then((visuals: any[]) => {
          const table = visuals?.find((v: any) => v.title === HIDDEN_URL_TABLE_VISUAL_NAME);
          this.cachedHiddenTableVisual = table ?? null;
        }, () => { this.cachedHiddenTableVisual = null; });
      }, () => { this.cachedHiddenTableVisual = null; });
    });
    this.eventHandlers.set('dataHyperlinkClicked', (event) => {
      this.onDataHyperlinkClicked(event);
    });
    this.eventHandlers.set('buttonClicked', (event) => {
      this.onButtonClicked(event);
    });

    this.reportParams = this.getReportParamsFromUrlOrStorage() ?? {};
    this.reportTabs = [];
    this.reportService.getPowerBIMisReport(undefined, Object.keys(this.reportParams).length > 0 ? this.reportParams : undefined).subscribe({
      next: reports => {
        if (reports?.results?.length > 0) {
          this.buildReportConfigMap(reports.results);
          // Add Dashboard tab if present
          const dashboardEntry = Array.from(this.reportConfigByReportId.values())
            .find(e => e.reportName?.trim().toUpperCase() === 'DASHBOARD');
          if (dashboardEntry) {
            this.reportTabs.push({
              reportId: dashboardEntry.reportId,
              label: 'Dashboard',
              embedConfig: dashboardEntry
            });
          }
          // Add other report tabs
          for (const entry of Array.from(this.reportConfigByReportId.values())) {
            if (entry.reportName?.trim().toUpperCase() !== 'DASHBOARD') {
              this.reportTabs.push({
                reportId: entry.reportId,
                label: entry.reportName?.trim() || entry.reportId,
                embedConfig: entry
              });
            }
          }
          // applyEmbedConfig sets reportConfig.id, which adds the powerbi-report component
          // to the DOM (via *ngIf="reportConfig.id"). Power BI then loads and fires loaded/rendered.
          if (this.reportTabs.length > 0) {
            this.selectedReportTabIndex = 0;
            this.applyEmbedConfig(this.reportTabs[0].embedConfig);
          }
        }
      },
      error: error => {
        console.error("Error fetching Power BI MIS report:", error);
      }
    });
  }

  private buildReportConfigMap(results: Array<{ embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>; embedToken?: { token?: string } }>): void {
    populateReportConfigMap(this.reportConfigByReportId, results);
    // Register all non-Dashboard report names so tabs.component.ts can recognise
    // linked report tabs as report tabs (not message tabs) in handleChange.
    for (const item of results) {
      for (const r of (item.embedReports ?? [])) {
        const name = r.reportName?.trim();
        if (name && name.toUpperCase() !== 'DASHBOARD') {
          this.tabDataService.misReportTabNames.add(name);
        }
      }
    }
  }

  /** Merge new entries into the map without clearing existing ones (preserves Dashboard token). */
  private mergeReportConfigMap(results: Array<{ embedReports?: Array<{ reportId?: string; embedUrl?: string; reportName?: string }>; embedToken?: { token?: string } }>): void {
    for (const item of results) {
      if (!item.embedReports?.length || !item.embedToken?.token) continue;
      for (const r of item.embedReports) {
        if (r.reportId && r.embedUrl) {
          this.reportConfigByReportId.set(r.reportId, {
            reportId: r.reportId,
            embedUrl: r.embedUrl,
            accessToken: item.embedToken.token,
            reportName: r.reportName
          });
          // Also register the name so handleChange can recognise the opened tab
          const name = r.reportName?.trim();
          if (name && name.toUpperCase() !== 'DASHBOARD') {
            this.tabDataService.misReportTabNames.add(name);
          }
        }
      }
    }
  }

  /** Apply an embed entry as the current reportConfig (for the Dashboard display). */
  private applyEmbedConfig(entry: ReportEmbedEntry, rpParams?: Record<string, string | string[]>): void {
    const params = rpParams ?? this.reportParams;
    let embedUrl = entry.embedUrl;
    if (Object.keys(params).length > 0) {
      const query = buildReportParamsQueryString(params);
      if (query) {
        const joinChar = embedUrl.includes('?') ? '&' : '?';
        embedUrl = embedUrl + joinChar + query + '&toolbar=false';
      }
    }
    // Use the cluster URL as hostname so the Power BI JS API (getPages, exportData) can
    // complete its postMessage round-trip. Without this, api calls hang because the iframe
    // loads from analysis.windows.net but the SDK targets app.powerbi.com.
    const hostname = this.extractClusterUrl(entry.embedUrl) ?? this.reportConfig.hostname;
    this.reportConfig = { ...this.reportConfig, id: entry.reportId, embedUrl, accessToken: entry.accessToken, hostname };
  }

  /**
   * Extract the Power BI cluster URL from the `config` query param of an embedUrl.
   * The config param is a base64-encoded JSON containing `{ clusterUrl: "https://WABI-..." }`.
   * This cluster URL must be used as the embed `hostname` so the JS API postMessage round-trips
   * target the correct origin (the iframe loads from the cluster, not app.powerbi.com).
   */
  private extractClusterUrl(embedUrl: string): string | undefined {
    try {
      const configParam = new URL(embedUrl).searchParams.get('config');
      if (!configParam) return undefined;
      const config = JSON.parse(atob(configParam));
      const url = config?.clusterUrl as string | undefined;
      return url?.startsWith('https://') ? url : undefined;
    } catch {
      return undefined;
    }
  }

  /** Get rp:* params from URL (same as Inquiry flow). */
  private getReportParamsFromUrlOrStorage(): Record<string, string | string[]> | undefined {
    return getReportParamsFromUrlOrStorage();
  }

  private findReportEntryById(reportId: string): ReportEmbedEntry | undefined {
    return findReportEntryById(this.reportConfigByReportId, reportId);
  }

  private onDataHyperlinkClicked(event?: { detail?: { url?: string } }): void {
    const url = event?.detail?.url;
    if (!url) { return; }
    const reportId = this.parseReportIdFromUrl(url);
    if (!reportId) { return; }
    if (this.fetchInProgress.has(reportId)) { return; }
    this.showSpinner = true;
    const rpParams = this.parseRpParamsFromUrl(url);
    this.fetchInProgress.add(reportId);
    this.reportService.getPowerBIMisReport(reportId).subscribe({
      next: reports => {
        this.fetchInProgress.delete(reportId);
        if (!reports?.results?.length) {
          this.showSpinner = false;
          return;
        }
        this.mergeReportConfigMap(reports.results);
        const entry = this.findReportEntryById(reportId);
        if (entry) {
          this.openReportInNewTab(entry, rpParams);
        } else {
          this.showSpinner = false;
        }
      },
      error: err => {
        this.fetchInProgress.delete(reportId);
        this.showSpinner = false;
        console.error('[MIS Report] onDataHyperlinkClicked: fetch failed for reportId', reportId, err);
      }
    });
  }

  /**
   * Button clicked inside the embed.
   * - Title-mapped buttons: look up the column in HiddenURLTable by BUTTON_TITLE_TO_COLUMN,
   *   extract the fully-formed URL (reportId + rp:* params) from that column, then delegate
   *   to onDataHyperlinkClicked which handles the full fetch-and-open flow.
   * - Web URL buttons with a reportId in the URL: treated same as hyperlink click.
   * - Other types: ignored (Power BI handles natively).
   */
  private onButtonClicked(event?: { detail?: { title?: string; id?: string; type?: string; bookmark?: string; url?: string } }): void {
    const type = event?.detail?.type;
    const title = event?.detail?.title?.trim();
    const url = (event?.detail as any)?.url as string | undefined;

    // Web URL button — delegate to hyperlink handler (URL has rp:* params)
    if ((type === 'Web' || type === 'WebUrl') && url) {
      const reportId = this.parseReportIdFromUrl(url);
      if (reportId) this.onDataHyperlinkClicked({ detail: { url } });
      return;
    }

    if (!title) { return; }

    const columnName = BUTTON_TITLE_TO_COLUMN[title.toUpperCase()];
    if (!columnName) { return; }

    this.showSpinner = true;

    const proceed = (columnUrl: string | null) => {
      if (!columnUrl) {
        this.showSpinner = false;
        return;
      }
      this.onDataHyperlinkClicked({ detail: { url: columnUrl } });
    };

    // Use pre-warmed visual cache from rendered event if available.
    // `undefined` = cold (rendered not yet fired) → fall back to on-demand read with timeout.
    // `null`      = rendered fired but visual not found → cannot proceed.
    // object      = visual found → exportData() to read column URL.
    if (this.cachedHiddenTableVisual === undefined) {
      const timeoutPromise = new Promise<null>(resolve => setTimeout(() => resolve(null), 12000));
      Promise.race([this.readUrlFromHiddenTableColumn(columnName), timeoutPromise]).then(u => proceed(u));
    } else if (this.cachedHiddenTableVisual === null) {
      proceed(null);
    } else {
      this.exportColumnUrlFromVisual(this.cachedHiddenTableVisual, columnName).then(u => proceed(u));
    }
  }

  /**
   * Cold-cache fallback: full getPages → getVisuals → exportData flow.
   * Also warms the visual cache as a side-effect.
   */
  private async readUrlFromHiddenTableColumn(columnName: string): Promise<string | null> {
    try {
      if (!this.embeddedReport) {
        return null;
      }
      const pages: any[] = await this.embeddedReport.getPages();
      const activePage = pages?.find((p: any) => p.isActive);
      if (!activePage) {
        return null;
      }
      const visuals: any[] = await activePage.getVisuals();
      const table = visuals?.find((v: any) => v.title === HIDDEN_URL_TABLE_VISUAL_NAME);
      if (!table) {
        this.cachedHiddenTableVisual = null;
        return null;
      }
      this.cachedHiddenTableVisual = table;
      return await this.exportColumnUrlFromVisual(table, columnName);
    } catch {
      return null;
    }
  }

  /**
   * Export the URL from a specific column in the HiddenURLTable visual.
   * exportData() returns CSV: row 0 = headers, row 1 = values.
   * Finds the column by name and returns its value.
   */
  private async exportColumnUrlFromVisual(visual: any, columnName: string): Promise<string | null> {
    try {
      const exported = await visual.exportData();
      const raw = exported.data as string;
      const rows = raw.split('\r\n').filter((r: string) => r.trim());
      if (rows.length < 2) {
        return null;
      }
      const headers = this.parseCsvRow(rows[0]);
      const values  = this.parseCsvRow(rows[1]);
      const colIndex = headers.findIndex(h => h.trim() === columnName);
      if (colIndex === -1) {
        return null;
      }
      return values[colIndex]?.trim() || null;
    } catch {
      return null;
    }
  }

  /** Minimal CSV row parser — handles double-quote escaping and quoted fields containing commas. */
  private parseCsvRow(row: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const ch = row[i];
      if (ch === '"') {
        if (inQuotes && row[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  }

  /**
   * Open a linked report as a new in-app tab (same pattern as Inquiry Reports).
   * Does NOT mutate reportConfig — uses TabDataService.sendTabData() instead.
   */
  private openReportInNewTab(entry: ReportEmbedEntry, rpParams?: Record<string, string | string[]>): void {
    const tabName = entry.reportName?.trim() || entry.reportId;
    const params = (rpParams && Object.keys(rpParams).length > 0) ? rpParams : this.reportParams;
    let embedUrl = entry.embedUrl;
    if (Object.keys(params).length > 0) {
      const query = buildReportParamsQueryString(params);
      if (query) {
        const joinChar = embedUrl.includes('?') ? '&' : '?';
        embedUrl = embedUrl + joinChar + query + '&toolbar=false';
      }
    }
    const reportConfig = { ...this.reportConfig, id: entry.reportId, embedUrl, accessToken: entry.accessToken };
    this.showSpinner = false;
    this.tabDataService.sendTabData(tabName, 0, 'report', { reportConfig }, true);
  }

  /**
   * Extract all rp:* query params from a URL into a Record.
   * Multi-value params are deduplicated and collected into an array.
   * Example: "&rp:pStart_Date=2025-01-01&rp:pGroup=G1&rp:pGroup=G2"
   *   → { 'rp:pStart_Date': '2025-01-01', 'rp:pGroup': ['G1', 'G2'] }
   */
  private parseRpParamsFromUrl(url: string): Record<string, string | string[]> {
    // Power BI SDK sometimes delivers the URL with HTML-encoded ampersands (&amp; instead of &).
    // Unescape before parsing so URL.searchParams receives proper separators.
    const cleanUrl = url.replace(/&amp;/g, '&');
    const params: Record<string, string[]> = {};
    try {
      const parsedUrl = cleanUrl.startsWith('http') ? cleanUrl : `https://app.powerbi.com${cleanUrl}`;
      const u = new URL(parsedUrl);
      u.searchParams.forEach((value, key) => {
        if (key.startsWith('rp:')) {
          if (!params[key]) params[key] = [];
          params[key].push(value);
        }
      });
    } catch {
      // Fallback: manual regex extraction for non-standard URLs
      const re = /[?&](rp:[^=]+)=([^&]*)/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(cleanUrl)) !== null) {
        const key = decodeURIComponent(m[1]);
        const value = decodeURIComponent(m[2]);
        if (!params[key]) params[key] = [];
        params[key].push(value);
      }
    }
    // Deduplicate values per key, then flatten single-value arrays to strings.
    return Object.fromEntries(
      Object.entries(params).map(([k, v]) => {
        const unique = [...new Set(v)];
        return [k, unique.length === 1 ? unique[0] : unique];
      })
    );
  }

  private parseReportIdFromUrl(url: string): string | null {
    try {
      if (url.includes('reportId=')) {
        const m = /reportId=([a-fA-F0-9-]+)/.exec(url);
        return m ? m[1] : null;
      }
      const u = new URL(url, 'https://app.powerbi.com');
      const fromQuery = u.searchParams.get('reportId');
      if (fromQuery) return fromQuery;
      const pathM = /\/reports\/([a-fA-F0-9-]+)/.exec(u.pathname);
      return pathM ? pathM[1] : null;
    } catch {
      return null;
    }
  }
}